import type { ReactNode } from "react";
import { useT } from "@transifex/react";
import { useHistory } from "react-router-dom";
import testCreationImg from "../../assets/img/teacherTools/grammarTrainer.webp";
import vocabTrainerImg from "../../assets/img/teacherTools/vocabTrainer.webp";
import BasePopupWrapper, { TitleColorVariants } from "../../basic/popups/BasePopupWrapper";
import { getCurrentDateTimeFormatted } from "../../helpers/DateTime";
import { CreationModes } from "../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { useTestCreationContext } from "../../context/TestCreationContext";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import CreateOptionCard from "./CreateOptionCard";
import { useLogger } from "../../hooks/useLogger";

interface CreatePopupProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    onOptionSelected?: () => void;
    children?: ReactNode;
}

export const CreatePopup = (props: CreatePopupProps) => {
    const { setCreatingMode } = useGeneralContext();
    const { setTestName, setSelectedTitleTag } = useTestCreationContext();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const { logEvent } = useLogger();
    const history = useHistory();
    const t = useT();

    const t_testAssistantTitle = t("Create test", {
        _tags: "TeacherArea",
    });
    const t_testAssistantSubtitle = t("Configure and customize your vocabulary tests efficiently", {
        _tags: "TeacherArea",
    });
    const t_alt_testAssistant = t(
        "An image of a test being created. A new gap-sentence exercise is added to the test.",
        {
            _tags: "TeacherArea,ScreenReaderOnly",
        }
    );

    const t_vocabTrainerAssistantTitle = t("Assign vocabulary", {
        _tags: "TeacherArea",
    });
    const t_vocabTrainerAssistantSubtitle = t("Assign targeted vocabulary to your students for training", {
        _tags: "TeacherArea",
    });
    const t_alt_vocabTrainerAssistant = t(
        "An image showing a list of selected words which will be assigned to the students to learn.",
        {
            _tags: "TeacherArea,ScreenReaderOnly",
        }
    );

    const t_whatToCreate = t("What would you like to create?", { _tags: "TeacherArea" });
    const t_cancel = t("Cancel", { _tags: "TeacherArea" });
    const t_defaultTestName = t("Test from {currentDate}", {
        currentDate: getCurrentDateTimeFormatted(),
        _tags: "CreateTest,CreateHome",
    });

    const handleCreateTest = () => {
        logEvent("Test Creation Started", {
            entry_point: "create_popup",
            creating_mode: CreationModes.TEST,
        });
        props.onClose();
        props.onOptionSelected?.();
        setCreatingMode(CreationModes.TEST);
        setTestName(t_defaultTestName);
        history.push("/create/choose-source");
    };

    const handleCreateVocabTraining = () => {
        props.onClose();
        props.onOptionSelected?.();
        setCreatingMode(CreationModes.RECURRING_TASK);
        setTestName("");
        setSelectedTitleTag(null);
        history.push("/create/choose-vocab-training-source");
    };

    const createPopupOptions = [
        {
            "data-testid": "create-popup-option-vocab-training",
            title: t_vocabTrainerAssistantTitle,
            subtitle: t_vocabTrainerAssistantSubtitle,
            onClick: handleCreateVocabTraining,
            image: { src: vocabTrainerImg, alt: t_alt_vocabTrainerAssistant },
            borderColor: "border-forest-green-800",
            titleColor: "text-forest-green-800",
        },
        {
            "data-testid": "create-popup-option-test",
            title: t_testAssistantTitle,
            subtitle: t_testAssistantSubtitle,
            onClick: handleCreateTest,
            image: { src: testCreationImg, alt: t_alt_testAssistant },
            borderColor: "border-p6-orange",
            titleColor: "text-p6-orange",
        },
    ];

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={props.title ?? t_whatToCreate}
            titleColor={TitleColorVariants.NEUTRAL}
            secondaryButton={{
                text: t_cancel,
                onClick: props.onClose,
            }}
        >
            <div className="flex flex-col gap-m">
                <div
                    className={`flex items-stretch ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "flex-row",
                        "flex-col gap-s"
                    )}`}
                >
                    {createPopupOptions.map((option, index) => (
                        <CreateOptionCard
                            key={`create-option-${index}`}
                            option={option}
                            showChevron={index === 0}
                        />
                    ))}
                </div>

                {props.children}
            </div>
        </BasePopupWrapper>
    );
};
